/**
 * iter86.31 — Golf leaderboard (both golf modes).
 * iter86.31.1 — Labeled breakdown columns (Total Strokes / Handicap /
 * Completed Rounds / Avg per Round) + TOTALS row; Power Pick highlighted
 * blue; Sync moved to the tournament-admin Live Sync tab.
 */
import { useState } from "react";
import { Trophy, Crown, ChevronDown, ChevronUp, Lock, Zap } from "lucide-react";
import { FullscreenShell, ExpandButton, SwipeScroll } from "@/components/common/ExpandablePane";
import { isGolfPickem } from "@/lib/tournamentFormat";

const ROUND_LABELS = { 0: "Not started", 1: "Round 1", 2: "Round 2", 3: "Round 3", 4: "Round 4" };
const OUT_LABELS = { cut: "CUT", wd: "WD", dq: "DQ", dnf: "DNF" };

export default function GolfLeaderboard({ board, tournament, meId }) {
    const [expandedId, setExpandedId] = useState(null);
    const [fullscreenId, setFullscreenId] = useState(null);
    const pickem = isGolfPickem(tournament);
    const golf = tournament?.golf || {};

    // iter87.6 — Admin lock/unlock moved to Tournament Admin →
    // Danger Zone. This component keeps only the read-only "locked by
    // admin" status pill so players see the state.
    const adminLocked = !!golf.picks_locked_by_admin;

    return (
        <div className="space-y-4">
            {/* Live round status strip */}
            <div data-testid="golf-round-strip" className="brutal-card p-4 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xl">⛳</span>
                    <div>
                        <p className="font-display font-extrabold uppercase tracking-tight text-sm">
                            {golf.event_name || "Golf Tournament"} {golf.season ? `· ${golf.season}` : ""}
                        </p>
                        <p className="text-[11px] text-zinc-500">
                            {ROUND_LABELS[golf.current_round] || `Round ${golf.current_round}`} — {golf.round_status || "Not Started"}
                            {golf.cut_made && <span className="text-blaze ml-2">· Cut made</span>}
                        </p>
                    </div>
                </div>
                {golf.last_synced_at && (
                    <span className="text-[10px] text-zinc-600 font-mono" data-testid="golf-last-synced">
                        updated {new Date(golf.last_synced_at).toLocaleTimeString()}
                    </span>
                )}
                {pickem && adminLocked && !golf.r1_started && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-cyber/60 bg-cyber/10 text-cyber text-[9px] font-bold px-2 py-0.5" data-testid="golf-admin-locked-pill">
                        <Lock className="w-3 h-3" /> PICKS LOCKED BY ADMIN
                    </span>
                )}
                {/* iter87.6 — "Lock crew picks" button moved to Tournament
                    Admin → Danger Zone. The status pill above still gives
                    the leaderboard-strip a signal for players + admins. */}
            </div>

            {/* Player leaderboard — iter87.25: outer card + "LEADERBOARD"
                h3 removed to match every other Cheer plane (Takeover
                header already carries the title). Round strip above is
                a distinct summary tile and stays. */}
            <div>
                <div className="flex items-center justify-end mb-3 gap-2 flex-wrap">
                    <span
                        data-testid="golf-scoring-mode-pill"
                        className="inline-flex items-center rounded-full border border-cyber/60 bg-cyber/10 text-cyber text-[10px] uppercase font-bold tracking-wider px-2.5 py-0.5"
                    >
                        {pickem ? "Lowest Average Wins" : "Position Points"}
                    </span>
                </div>
                <div className="space-y-2">
                    {board.length === 0 && (
                        <p className="text-zinc-500 text-sm text-center py-6">
                            Standings appear once crewmates lock in their golfers.
                        </p>
                    )}
                    {board.map(row => {
                        const isMe = row.participant_id === meId;
                        const isOpen = expandedId === row.participant_id;
                        // Bento rank chip tone parity with LeaderboardTable (podium colors).
                        const RANK_STYLES = {
                            1: "border-cyber/60 text-cyber bg-cyber/15",
                            2: "border-white/25 text-zinc-100 bg-white/[0.06]",
                            3: "border-blaze/60 text-blaze bg-blaze/15",
                        };
                        const rankClass = RANK_STYLES[row.rank] || "border-white/[0.10] text-zinc-400 bg-white/[0.03]";
                        // Podium halo — matched to LeaderboardTable; guarded by "any player scored yet"
                        const podiumEligible = pickem
                            ? (row.golf?.avg_score != null)
                            : (row.total_points > 0);
                        const PODIUM_HALO = {
                            1: "shadow-[inset_0_0_0_1px_rgba(212,246,68,0.35),0_0_28px_-8px_rgba(212,246,68,0.45)]",
                            2: "shadow-[inset_0_0_0_1px_rgba(228,228,231,0.30),0_0_24px_-8px_rgba(228,228,231,0.28)]",
                            3: "shadow-[inset_0_0_0_1px_rgba(255,107,74,0.35),0_0_24px_-8px_rgba(255,107,74,0.38)]",
                        };
                        const halo = podiumEligible ? (PODIUM_HALO[row.rank] || "") : "";
                        const ORDINAL = { 1: "1st", 2: "2nd", 3: "3rd" };
                        const RIBBON_TONE = {
                            1: "bg-cyber text-black",
                            2: "bg-white text-black",
                            3: "bg-blaze text-black",
                        };
                        const ordinal = podiumEligible ? ORDINAL[row.rank] : undefined;
                        const ribbonTone = RIBBON_TONE[row.rank];

                        const g = row.golf || {};
                        const headline = pickem
                            ? (g.avg_score != null ? g.avg_score.toFixed(2) : "—")
                            : row.total_points;
                        const sub = pickem ? "avg / round" : "pts";
                        return (
                            <div key={row.participant_id} data-testid={`golf-lb-row-${row.participant_id}`}>
                                <button
                                    type="button"
                                    data-testid={`golf-lb-expand-${row.participant_id}-btn`}
                                    onClick={() => setExpandedId(isOpen ? null : row.participant_id)}
                                    className={`w-full flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-lg border text-left transition-all duration-150 active:scale-[0.99] ${isMe ? "border-cyber/60 bg-cyber/[0.08] hover:bg-cyber/[0.12]" : "border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-white/[0.16]"} ${halo} ${isOpen ? "rounded-b-none" : ""}`}
                                >
                                    {/* Rank chip — circular Bento pill with podium ribbon */}
                                    <div className="shrink-0 relative">
                                        {ordinal && (
                                            <span
                                                data-testid={`golf-podium-ribbon-${row.participant_id}`}
                                                className={`absolute -top-2 left-1/2 -translate-x-1/2 rounded-full ${ribbonTone} text-[8px] font-black uppercase tracking-[0.15em] px-1.5 py-[1px] shadow-[0_1px_2px_rgba(0,0,0,0.35)]`}
                                                aria-label={`${ordinal} place`}
                                            >
                                                {ordinal}
                                            </span>
                                        )}
                                        <div
                                            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full border ${rankClass} flex items-center justify-center font-mono font-black text-sm sm:text-base`}
                                            title={`Rank #${row.rank}`}
                                        >
                                            {row.rank}
                                        </div>
                                    </div>
                                    {/* Avatar chip — matches LeaderboardTable */}
                                    <div className={`shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center text-lg sm:text-xl ${isMe ? "border-cyber/40 bg-cyber/[0.06]" : "border-white/10 bg-white/[0.04]"}`}>
                                        {row.avatar_emoji || <span className="text-zinc-600 text-base">🙂</span>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <p className="font-bold text-sm sm:text-base truncate">{row.username}</p>
                                            {row.is_admin && <Crown className="w-3 h-3 text-cyber shrink-0" />}
                                            {row.picks_hidden && (row.picks_count || 0) > 0 ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] border border-white/10 text-zinc-400 text-[9px] font-bold px-2 py-0.5" data-testid={`golf-picks-hidden-pill-${row.participant_id}`}>
                                                    <Lock className="w-2.5 h-2.5" /> PICKS IN — HIDDEN
                                                </span>
                                            ) : (row.team_breakdown || []).length === 0 && (
                                                <span className="inline-flex items-center rounded-full bg-blaze/15 border border-blaze/60 text-blaze text-[9px] font-bold px-2 py-0.5" data-testid={`golf-no-picks-pill-${row.participant_id}`}>
                                                    NO PICKS
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 mt-1 text-[10px] leading-tight">
                                            {(row.team_breakdown || []).slice(0, 6).map(tb => (
                                                <span key={tb.team_id} className={tb.eliminated ? "text-zinc-600 line-through" : tb.is_power_pick ? "text-sky-400" : "text-zinc-500"}>
                                                    {tb.team_name}{tb.is_power_pick ? " ⚡" : ""}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="shrink-0 text-right border-l border-white/[0.08] ml-1 sm:ml-2 pl-2 sm:pl-3">
                                        <div className="flex items-center justify-end gap-1">
                                            {!pickem && (g.points_delta || 0) !== 0 && (
                                                <span
                                                    data-testid={`golf-points-delta-${row.participant_id}`}
                                                    className={`font-mono text-[11px] font-bold ${g.points_delta > 0 ? "text-neon" : "text-heat"}`}
                                                    title="Points change since the round started"
                                                >
                                                    {g.points_delta > 0 ? `▲${g.points_delta}` : `▼${Math.abs(g.points_delta)}`}
                                                </span>
                                            )}
                                            <div className="font-mono font-black text-2xl sm:text-3xl leading-none text-white" data-testid={`golf-headline-${row.participant_id}`}>{headline}</div>
                                        </div>
                                        <div className="text-[9px] uppercase tracking-widest text-zinc-600 leading-none mt-1">{sub}</div>
                                    </div>
                                    {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />}
                                </button>
                                {isOpen && (row.picks_hidden ? (
                                    <div className={`rounded-b-lg border border-t-0 ${isMe ? "border-cyber/60" : "border-white/[0.08]"} bg-[#0e0e0e] px-4 py-4 text-center -mt-px`} data-testid={`golf-breakdown-hidden-${row.participant_id}`}>
                                        <p className="text-xs text-zinc-500 flex items-center justify-center gap-1.5">
                                            <Lock className="w-3.5 h-3.5" />
                                            Golfer picks are hidden until the game locks (first tee-off, or the admin locks the draft).
                                        </p>
                                    </div>
                                ) : (
                                    <GolferBreakdown row={row} pickem={pickem} golfCfg={golf} teams={tournament.teams} onExpand={() => setFullscreenId(row.participant_id)} isMe={isMe} />
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* iter87.24 — Deleted the "Tournament Champion" winner banner
                (formerly rendered here based on `golf.winner_team_id`). The
                banner labelled the golfer who took the trophy as the
                "Tournament Champion", which was misleading in a Cheer /
                Leaderboard modal that lists CREWMATE standings. The
                top-of-table crewmate row already carries the podium halo
                and gold "1st" ribbon — that is the actual rivalry
                champion. See iter87.23 for the parallel delete on the
                non-golf `LeaderboardView`. */}

            {fullscreenId && (() => {
                const row = board.find(r => r.participant_id === fullscreenId);
                if (!row) return null;
                return (
                    <GolfBreakdownFullscreen
                        row={row}
                        pickem={pickem}
                        golfCfg={golf}
                        teams={tournament.teams}
                        onClose={() => setFullscreenId(null)}
                    />
                );
            })()}
        </div>
    );
}

// iter86.77.11 — Full-screen breakdown view: hides the header ribbon so
// the whole golfer table is visible on phones.
function GolfBreakdownFullscreen({ row, pickem, golfCfg, teams, onClose }) {
    const g = row.golf || {};
    const headline = pickem
        ? (g.avg_score != null ? g.avg_score.toFixed(2) : "—")
        : row.total_points;
    return (
        <FullscreenShell
            testId={`golf-fullscreen-${row.participant_id}`}
            onClose={onClose}
            header={
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="font-mono font-black text-xl text-cyber shrink-0">#{row.rank}</div>
                    <span className="text-lg leading-none shrink-0">{row.avatar_emoji || "🙂"}</span>
                    <p className="font-bold truncate flex-1 min-w-0">{row.username}</p>
                    <div className="text-right shrink-0">
                        <div className="font-mono font-black text-xl text-white leading-none">{headline}</div>
                        <div className="text-[8px] uppercase tracking-widest text-zinc-500 font-mono">{pickem ? "avg / round" : "pts"}</div>
                    </div>
                </div>
            }
        >
            <GolferBreakdown row={row} pickem={pickem} golfCfg={golfCfg} teams={teams} fullscreen />
        </FullscreenShell>
    );
}

function GolferBreakdown({ row, pickem, golfCfg, teams, onExpand, fullscreen, isMe }) {
    const breakdown = row.team_breakdown || [];
    // iter86.77.10 — final-round projection columns (LAS only, while R4 live)
    const hasProj = pickem && row.golf?.projecting;
    // iter87.165 — Non-proj modes get fixed-width numeric columns so
    // the golfer breakdown aligns with LeaderboardTable + GolfStandings.
    // hasProj is a rare R4-live edge case; keep it on the proportional
    // 17-col grid until we redesign the projection layout.
    const gridCls = hasProj ? "grid-cols-[repeat(17,minmax(0,1fr))]" : "";
    const gridStyle = hasProj
        ? undefined
        : {
            gridTemplateColumns: pickem
                ? "minmax(120px,1fr) 56px 88px 80px 56px 72px"      // Golfer|Pos|TotStrokes|Handicap|Rds|Avg
                : "minmax(120px,1fr) 56px 56px 72px 72px 72px",     // Golfer|Pos|Mvmt|Cut|Pos Pts|Total
        };
    const hasCut = hasProj && breakdown.some(tb => tb.golf_projection === "cut");
    const totals = breakdown.reduce(
        (acc, tb) => {
            acc.strokes += tb.golf_total_strokes || 0;
            acc.handicap += tb.golf_power_shave || 0;
            acc.rounds += tb.golf_rounds_played || 0;
            acc.points += tb.total || 0;
            return acc;
        },
        { strokes: 0, handicap: 0, rounds: 0, points: 0 },
    );
    const champBonus = pickem ? (row.bonus_points || 0) : 0;
    const totalAvg = row.golf?.avg_score != null
        ? row.golf.avg_score.toFixed(2)
        : (totals.rounds > 0 ? ((totals.strokes - totals.handicap - champBonus) / totals.rounds).toFixed(2) : "—");

    return (
        <div
            data-testid={`golf-breakdown-${row.participant_id}`}
            className={fullscreen
                ? "px-1 py-2 space-y-2"
                : `rounded-b-lg border border-t-0 ${isMe ? "border-cyber/60" : "border-white/[0.08]"} bg-[#0e0e0e] px-3 py-3 space-y-2 -mt-px`}
        >
            <div className="flex items-center justify-between">
                <h5 className="text-[9px] uppercase tracking-widest text-zinc-500">My Golfers</h5>
                {onExpand && (
                    <ExpandButton onClick={onExpand} testId={`golf-breakdown-expand-${row.participant_id}-btn`} />
                )}
            </div>
            <SwipeScroll minWidth={pickem ? (hasProj ? 700 : 480) : 480}>
            <div className="space-y-2">
            {/* Column labels */}
            <div
                className={`grid ${gridCls} gap-3 pb-1 border-b border-[#222] text-[8px] uppercase tracking-widest text-zinc-600 font-bold`}
                style={gridStyle}
            >
                <div className={hasProj ? "col-span-4" : ""}>Golfer</div>
                <div className={`text-center ${hasProj ? "col-span-1" : ""}`}>Pos</div>
                {pickem ? (
                    <>
                        <div className={`text-right ${hasProj ? "col-span-2" : ""}`}>Total Strokes</div>
                        <div className={`text-right text-sky-500 ${hasProj ? "col-span-2" : ""}`}>Handicap</div>
                        <div className={`text-right ${hasProj ? "col-span-1" : ""}`}>Rds</div>
                        <div className={`text-right ${hasProj ? "col-span-2" : ""}`}>Avg / Rd</div>
                        {hasProj && (
                            <>
                                <div className="col-span-1 text-right text-amber-500">Proj Rd</div>
                                <div className="col-span-2 text-right text-amber-500">Proj Total</div>
                                <div className="col-span-2 text-right text-amber-500">Proj LAS</div>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <div className="text-center">Mvmt</div>
                        <div className="text-right text-volt">Cut</div>
                        <div className="text-right">Pos Pts</div>
                        <div className="text-right">Total</div>
                    </>
                )}
            </div>
            <div className="space-y-1">
                {breakdown.map(tb => {
                    const outLabel = OUT_LABELS[(tb.golf_status || "").toLowerCase()];
                    const rds = tb.golf_rounds_played || 0;
                    const strokes = tb.golf_total_strokes || 0;
                    const shave = tb.golf_power_shave || 0;
                    const avg = rds > 0 ? ((strokes - shave) / rds).toFixed(2) : "—";
                    return (
                        <div
                            key={tb.team_id}
                            data-testid={`golf-breakdown-golfer-${tb.team_id}`}
                            className={`grid ${gridCls} items-center gap-3 text-xs rounded px-0.5 ${tb.is_power_pick ? "bg-sky-500/10 border-l-2 border-sky-400" : ""}`}
                            style={gridStyle}
                        >
                            <div className={`flex items-center gap-1 truncate ${hasProj ? "col-span-4" : ""}`}>
                                <span className="shrink-0 text-[11px]">{tb.flag || "⛳"}</span>
                                <span className={outLabel ? "text-zinc-500 line-through" : tb.is_power_pick ? "text-sky-300" : "text-zinc-200"}>{tb.team_name}</span>
                                {tb.is_power_pick && <Zap className="w-3 h-3 text-sky-400 shrink-0" title="Power Pick" />}
                                {tb.golf_pot && <span className="text-[9px] text-zinc-600 shrink-0">P{tb.golf_pot}</span>}
                            </div>
                            <div className={`text-center text-[10px] uppercase text-zinc-500 ${hasProj ? "col-span-1" : ""}`}>
                                {outLabel ? <span className="text-blaze">{outLabel}</span> : (tb.golf_position || "—")}
                            </div>
                            {pickem ? (
                                <>
                                    <div className={`text-right font-mono text-zinc-300 ${hasProj ? "col-span-2" : ""}`}>{rds > 0 ? strokes : "—"}</div>
                                    <div className={`text-right font-mono text-sky-400 ${hasProj ? "col-span-2" : ""}`}>{shave > 0 ? `−${shave}` : "—"}</div>
                                    <div className={`text-right font-mono text-zinc-400 ${hasProj ? "col-span-1" : ""}`}>{rds}</div>
                                    <div className={`text-right font-mono font-bold text-cyber ${hasProj ? "col-span-2" : ""}`}>{avg}</div>
                                    {hasProj && (tb.golf_projection === "cut" ? (
                                        <div className="col-span-5 text-right font-mono text-[10px] text-zinc-600" data-testid={`golf-proj-cut-${tb.team_id}`}>CUT — no projection*</div>
                                    ) : (
                                        <>
                                            <div className={`col-span-1 text-right font-mono ${tb.golf_projection === "live" ? "text-amber-400 italic" : "text-zinc-300"}`} data-testid={`golf-proj-round-${tb.team_id}`}>
                                                {tb.golf_projected_round ?? "—"}
                                            </div>
                                            <div className={`col-span-2 text-right font-mono ${tb.golf_projection === "live" ? "text-amber-400 italic" : "text-zinc-300"}`} data-testid={`golf-proj-total-${tb.team_id}`}>
                                                {tb.golf_projected_total ?? "—"}
                                            </div>
                                            <div className={`col-span-2 text-right font-mono font-bold ${tb.golf_projection === "live" ? "text-amber-400 italic" : "text-cyber"}`} data-testid={`golf-proj-avg-${tb.team_id}`}>
                                                {tb.golf_projected_avg != null ? tb.golf_projected_avg.toFixed(2) : "—"}
                                            </div>
                                        </>
                                    ))}
                                </>
                            ) : (
                                <>
                                    <div className="text-center font-mono text-[10px]">
                                        {tb.golf_pos_delta > 0
                                            ? <span className="text-neon">▲{tb.golf_pos_delta}</span>
                                            : tb.golf_pos_delta < 0
                                                ? <span className="text-heat">▼{Math.abs(tb.golf_pos_delta)}</span>
                                                : <span className="text-zinc-700">—</span>}
                                    </div>
                                    <div className="text-right font-mono">
                                        {outLabel
                                            ? <span className="text-heat">0</span>
                                            : (tb.golf_cut_projected
                                                ? <span className="text-volt">{tb.golf_cut_points > 0 ? `+${tb.golf_cut_points}` : "0"}*</span>
                                                : tb.golf_made_cut
                                                    ? <span className="text-neon">+{tb.golf_cut_points}</span>
                                                    : <span className="text-zinc-600">{rds > 0 ? "0" : "—"}</span>)}
                                    </div>
                                    <div className="text-right font-mono text-zinc-300">
                                        {tb.golf_position_points > 0 ? `+${tb.golf_position_points}` : (rds > 0 ? "0" : "—")}
                                        {tb.golf_points_delta > 0
                                            ? <span className="text-neon text-[9px] ml-0.5">▲{tb.golf_points_delta}</span>
                                            : tb.golf_points_delta < 0
                                                ? <span className="text-heat text-[9px] ml-0.5">▼{Math.abs(tb.golf_points_delta)}</span>
                                                : null}
                                    </div>
                                    <div className="text-right font-mono font-bold text-cyber">+{tb.total}</div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            {/* Totals row */}
            <div
                data-testid={`golf-breakdown-totals-${row.participant_id}`}
                className={`grid ${gridCls} items-center gap-3 text-xs border-t-2 border-[#333] pt-1.5 font-bold`}
                style={gridStyle}
            >
                <div className={`text-[10px] uppercase tracking-widest text-zinc-400 ${hasProj ? "col-span-4" : ""}`}>Totals</div>
                <div className={hasProj ? "col-span-1" : ""} />
                {pickem ? (
                    <>
                        <div className={`text-right font-mono text-white ${hasProj ? "col-span-2" : ""}`}>{totals.rounds > 0 ? totals.strokes : "—"}</div>
                        <div className={`text-right font-mono text-sky-400 ${hasProj ? "col-span-2" : ""}`}>{(totals.handicap + champBonus) > 0 ? `−${totals.handicap + champBonus}` : "—"}</div>
                        <div className={`text-right font-mono text-zinc-300 ${hasProj ? "col-span-1" : ""}`}>{totals.rounds}</div>
                        <div className={`text-right font-mono text-cyber text-sm ${hasProj ? "col-span-2" : ""}`}>{totalAvg}</div>
                        {hasProj && (
                            <>
                                <div className="col-span-1" />
                                <div className="col-span-2 text-right font-mono text-amber-400" data-testid={`golf-proj-crew-total-${row.participant_id}`}>
                                    {row.golf?.projected_total_strokes ?? "—"}
                                </div>
                                <div className="col-span-2 text-right font-mono text-amber-400 text-sm" data-testid={`golf-proj-crew-avg-${row.participant_id}`}>
                                    {row.golf?.projected_avg_score != null ? row.golf.projected_avg_score.toFixed(2) : "—"}
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <div />
                        <div className="text-right font-mono text-volt">+{row.golf?.cut_points ?? 0}</div>
                        <div className="text-right font-mono text-zinc-300">+{row.golf?.position_points ?? 0}</div>
                        <div className="text-right font-mono text-cyber text-sm">+{row.total_points}</div>
                    </>
                )}
            </div>
            </div>
            </SwipeScroll>
            {hasProj && (
                <p className="text-[9px] text-amber-500/80 leading-snug" data-testid={`golf-proj-note-${row.participant_id}`}>
                    Italic = live projection: today's to-par + par for the remaining holes. Actuals replace projections as final rounds post.{hasCut ? " * Cut/WD golfers carry their posted rounds only — no Round 4 projection." : ""}
                </p>
            )}
            {!pickem && (
                <div className="space-y-1 border-t border-[#222] pt-1.5" data-testid={`golf-subtotals-${row.participant_id}`}>
                    <div className="flex items-center justify-between text-xs gap-2">
                        <span className="text-[9px] uppercase tracking-widest text-volt">
                            Subtotal A · Cutline Points{row.golf?.cut_official ? "" : " (projected make/miss)"}
                        </span>
                        <span className="font-mono font-bold text-volt" data-testid={`golf-subtotal-a-${row.participant_id}`}>+{row.golf?.cut_points ?? 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs gap-2">
                        <span className="text-[9px] uppercase tracking-widest text-zinc-400">
                            Subtotal B · Projected Final Round Points
                        </span>
                        <span className="font-mono font-bold text-zinc-200" data-testid={`golf-subtotal-b-${row.participant_id}`}>
                            +{row.golf?.position_points ?? 0}
                            {(row.golf?.points_delta || 0) > 0
                                ? <span className="text-neon text-[9px] ml-1">▲{row.golf.points_delta}</span>
                                : (row.golf?.points_delta || 0) < 0
                                    ? <span className="text-heat text-[9px] ml-1">▼{Math.abs(row.golf.points_delta)}</span>
                                    : null}
                        </span>
                    </div>
                    {(() => {
                        const champTeam = (teams || []).find(t => t.id === row.bonus_pick_team_id);
                        const champPts = golfCfg?.scoring?.champion_pick_points ?? 10;
                        const winnerDecided = !!golfCfg?.winner_team_id;
                        const hit = (row.bonus_points || 0) > 0;
                        return (
                            <div className="flex items-center justify-between text-xs gap-2" data-testid={`golf-subtotal-champ-${row.participant_id}`}>
                                <span className="text-[9px] uppercase tracking-widest text-cyber flex items-center gap-1">
                                    <Crown className="w-3 h-3" /> Champions Pick{champTeam ? ` · ${champTeam.name}` : ""}
                                </span>
                                <span className={`font-mono font-bold ${hit ? "text-neon" : "text-cyber"}`}>
                                    {!champTeam
                                        ? "—"
                                        : winnerDecided
                                            ? `+${row.bonus_points || 0}${hit ? " 🏆" : ""}`
                                            : <span>+0 <span className="text-[9px] text-zinc-500">(pays +{champPts} if they win)</span></span>}
                                </span>
                            </div>
                        );
                    })()}
                    <div className="flex items-center justify-between text-xs gap-2 border-t border-[#222] pt-1">
                        <span className="text-[9px] uppercase tracking-widest text-white font-bold">Total · A + B + Champions</span>
                        <span className="font-mono font-black text-cyber" data-testid={`golf-subtotal-total-${row.participant_id}`}>+{row.total_points}</span>
                    </div>
                </div>
            )}
            {!pickem && !row.golf?.cut_official && (
                <p className="text-[9px] text-volt/80 leading-snug" data-testid={`golf-cut-projected-note-${row.participant_id}`}>
                    * Cut points are PROJECTED off the live cutline{golfCfg?.projected_cut_label ? ` (${golfCfg.projected_cut_label})` : ""} — actual points award once the cut is official. Mvmt = spots gained/lost since the round started · ▲▼ = points swing since the round started.
                </p>
            )}
            {pickem && row.bonus_points > 0 && (
                <div className="flex items-center justify-between text-xs" data-testid={`golf-champ-bonus-${row.participant_id}`}>
                    <span className="text-[9px] uppercase tracking-widest text-neon">Champions Pick hit!</span>
                    <span className="font-mono font-bold text-neon">
                        −{row.bonus_points} strokes
                    </span>
                </div>
            )}
        </div>
    );
}
